import { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";

const GAP_PX = 4; // gap-1 = 0.25rem = 4px
const OVERFLOW_BADGE_WIDTH = 30;

interface UseTruncatedListOptions {
  itemCount: number;
  gapPx?: number;
  overflowIndicatorWidth?: number;
  keepMinOne?: boolean;
  maxVisibleItems?: number;
  recalcKey?: string;
}

export const useTruncatedList = ({
  itemCount,
  gapPx = GAP_PX,
  overflowIndicatorWidth = OVERFLOW_BADGE_WIDTH,
  keepMinOne = false,
  maxVisibleItems,
  recalcKey = "",
}: UseTruncatedListOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const initialVisibleCount =
    maxVisibleItems !== undefined ? Math.min(itemCount, maxVisibleItems) : itemCount;
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const frameRef = useRef<number | null>(null);

  const calculate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const containerWidth = container.offsetWidth;
    const items = Array.from(measure.children) as HTMLElement[];

    let usedWidth = 0;
    let count = 0;

    for (const item of items) {
      if (maxVisibleItems !== undefined && count >= maxVisibleItems) {
        break;
      }

      const itemWidth = item.offsetWidth;
      const widthWithItem = usedWidth + itemWidth + (count > 0 ? gapPx : 0);
      const remaining = itemCount - count - 1;
      const overflowSpace = remaining > 0 ? gapPx + overflowIndicatorWidth : 0;

      if (widthWithItem + overflowSpace > containerWidth) {
        break;
      }
      usedWidth = widthWithItem;
      count += 1;
    }

    let nextVisibleCount = count;

    if (keepMinOne && count === 0 && itemCount > 0 && items.length > 0) {
      const firstItemWidth = items[0].offsetWidth;
      const hasMultipleItems = itemCount > 1;

      if (hasMultipleItems) {
        const firstWithOverflow = firstItemWidth + gapPx + overflowIndicatorWidth;
        if (firstWithOverflow <= containerWidth) {
          nextVisibleCount = 1;
        } else if (overflowIndicatorWidth <= containerWidth) {
          nextVisibleCount = 0;
        }
      } else if (firstItemWidth <= containerWidth) {
        nextVisibleCount = 1;
      }
    }

    if (maxVisibleItems !== undefined) {
      nextVisibleCount = Math.min(nextVisibleCount, maxVisibleItems);
    }
    setVisibleCount(prev => (prev === nextVisibleCount ? prev : nextVisibleCount));
  }, [itemCount, gapPx, overflowIndicatorWidth, keepMinOne, maxVisibleItems]);

  const scheduleCalculate = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      calculate();
      frameRef.current = null;
    });
  }, [calculate]);

  useLayoutEffect(() => {
    scheduleCalculate();
  }, [scheduleCalculate, recalcKey]);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const observer = new ResizeObserver(() => {
      scheduleCalculate();
    });
    observer.observe(container);
    observer.observe(measure);
    return () => {
      observer.disconnect();
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [itemCount, recalcKey, scheduleCalculate]);

  return {
    containerRef,
    measureRef,
    visibleCount,
    hiddenCount: itemCount - visibleCount,
  };
};
